/**
 * Personal access token management: create, list, revoke.
 *
 * PATs allow authenticating `spacecode` in CI/CD pipelines
 * without going through the interactive OAuth flow.
 *
 * Usage:
 *   spacecode token create --name "github-actions"
 *   spacecode token list
 *   spacecode token revoke <id>
 *   SPACECODE_TOKEN=sct_abc123 spacecode -p "fix tests" -R owner/repo
 */

import type { Command } from 'commander';
import chalk from 'chalk';
import { requireAuth } from './auth.js';

export function registerTokenCommand(program: Command): void {
  const token = program
    .command('token')
    .description('Manage personal access tokens for CI/scripting');

  token
    .command('create')
    .description('Create a new personal access token')
    .requiredOption('--name <name>', 'A label for this token (e.g. "github-actions")')
    .action(async (options: { name: string }) => {
      try {
        const client = await requireAuth();
        const result = await client.createApiToken(options.name);

        console.log(chalk.green('\nToken created successfully!\n'));
        console.log(`  ${chalk.bold('Token:')}  ${result.token}`);
        console.log(`  ${chalk.bold('ID:')}     ${result.id}`);
        console.log(`  ${chalk.bold('Prefix:')} ${result.prefix}`);
        console.log();
        console.log(chalk.yellow('Save this token — it will not be shown again.'));
        console.log(chalk.gray('\nUsage in CI:'));
        console.log(chalk.gray(`  SPACECODE_TOKEN=${result.token} spacecode -p "your prompt" -R owner/repo`));
      } catch (error) {
        console.error(chalk.red(`Failed to create token: ${error instanceof Error ? error.message : String(error)}`));
        process.exit(1);
      }
    });

  token
    .command('list')
    .description('List active personal access tokens')
    .action(async () => {
      try {
        const client = await requireAuth();
        const { tokens } = await client.listApiTokens();

        if (tokens.length === 0) {
          console.log(chalk.gray('No active tokens. Create one with: spacecode token create --name <name>'));
          return;
        }

        console.log(chalk.bold(`\n  Active tokens (${tokens.length}):\n`));

        // Table header
        const idW = 36;
        const nameW = 20;
        const prefixW = 14;
        const createdW = 20;
        const usedW = 20;

        console.log(
          `  ${chalk.gray('ID'.padEnd(idW))}  ${chalk.gray('Name'.padEnd(nameW))}  ${chalk.gray('Prefix'.padEnd(prefixW))}  ${chalk.gray('Created'.padEnd(createdW))}  ${chalk.gray('Last Used'.padEnd(usedW))}`,
        );
        console.log(chalk.gray(`  ${'─'.repeat(idW + nameW + prefixW + createdW + usedW + 8)}`));

        for (const t of tokens) {
          const created = new Date(t.createdAt).toLocaleDateString();
          const lastUsed = t.lastUsedAt ? new Date(t.lastUsedAt).toLocaleDateString() : 'Never';
          const name = t.name.length > nameW ? t.name.slice(0, nameW - 1) + '…' : t.name;

          console.log(
            `  ${t.id.padEnd(idW)}  ${name.padEnd(nameW)}  ${chalk.cyan(t.prefix.padEnd(prefixW))}  ${chalk.gray(created.padEnd(createdW))}  ${chalk.gray(lastUsed.padEnd(usedW))}`,
          );
        }
        console.log();
      } catch (error) {
        console.error(chalk.red(`Failed to list tokens: ${error instanceof Error ? error.message : String(error)}`));
        process.exit(1);
      }
    });

  token
    .command('revoke <id>')
    .description('Revoke a personal access token')
    .action(async (id: string) => {
      try {
        const client = await requireAuth();
        await client.revokeApiToken(id);
        console.log(chalk.green(`Token ${id} revoked.`));
      } catch (error) {
        console.error(chalk.red(`Failed to revoke token: ${error instanceof Error ? error.message : String(error)}`));
        process.exit(1);
      }
    });
}
